/**
 * CAOSLAB.DEV — STANDALONE BLOG PAGE SCRIPT
 * Combines Static baseline posts and Realtime Cloud Firestore posts
 */

import { initChaosCanvas } from './canvas-chaos.js';
import { BLOG_POSTS } from './blog.js';
import { getPublishedPosts } from './firebase.js';

document.addEventListener('DOMContentLoaded', async () => {
  // Initialize canvas background
  initChaosCanvas();

  const gridContainer = document.getElementById('blog-posts-grid');
  const filterButtons = document.querySelectorAll('.blog-filter-btn');
  const searchInput = document.getElementById('blog-search-input');

  let currentCategory = 'all';
  let searchQuery = '';
  let allPosts = [...BLOG_POSTS];

  // Initial render with static posts
  renderPosts();

  // Fetch Firestore dynamic published posts
  try {
    const { posts: firestorePosts } = await getPublishedPosts();
    if (firestorePosts && firestorePosts.length > 0) {
      // Format firestore posts
      const formattedFirestore = firestorePosts.map(fp => {
        let dateStr = 'Reciente';
        if (fp.publishedAt?.toDate) {
          dateStr = fp.publishedAt.toDate().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        } else if (fp.createdAt?.toDate) {
          dateStr = fp.createdAt.toDate().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        }

        return {
          id: fp.id,
          slug: fp.slug || fp.id,
          category: fp.category || 'Desarrollo Web',
          categoryKey: fp.categoryKey || 'web',
          title: fp.title,
          excerpt: fp.excerpt,
          date: dateStr,
          readTime: fp.readTime || '5 min de lectura',
          coverImageUrl: fp.coverImageUrl || '',
          isFirestore: true
        };
      });

      // Merge: Avoid duplicates if slug/id matches
      const merged = [...formattedFirestore];
      BLOG_POSTS.forEach(sp => {
        if (!merged.some(m => m.slug === sp.id || m.id === sp.id)) {
          merged.push(sp);
        }
      });

      allPosts = merged;
      renderPosts();
    }
  } catch (err) {
    console.warn("Could not fetch Firestore posts:", err);
  }

  function renderPosts() {
    if (!gridContainer) return;

    let filtered = allPosts;

    if (currentCategory !== 'all') {
      filtered = filtered.filter(p => p.categoryKey === currentCategory);
    }

    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.title && p.title.toLowerCase().includes(q)) || 
        (p.excerpt && p.excerpt.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q))
      );
    }

    if (filtered.length === 0) {
      if (allPosts.length === 0) {
        gridContainer.innerHTML = `
          <div class="empty-blog-state glass-card cyber-corners" style="grid-column: 1 / -1; text-align: center; padding: 70px 24px; border: 1px dashed rgba(212,175,55,0.3); background: rgba(12,12,18,0.7); max-width: 800px; margin: 0 auto; width: 100%;">
            <div style="width: 72px; height: 72px; margin: 0 auto 20px auto; border-radius: 50%; background: rgba(212,175,55,0.1); border: 1px solid rgba(212,175,55,0.3); display: flex; align-items: center; justify-content: center;">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#d4af37" stroke-width="1.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </div>
            <span class="admin-badge" style="margin-bottom: 12px;">PUBLICACIONES PRÓXIMAMENTE</span>
            <h3 style="font-size: 1.5rem; color: #ffffff; margin-bottom: 12px; font-family: var(--font-heading);">Nuevos Artículos en Preparación</h3>
            <p style="color: var(--text-muted); max-width: 560px; margin: 0 auto 28px auto; font-size: 0.95rem; line-height: 1.6;">
              Estamos preparando publicaciones de ingeniería avanzada sobre <strong>GEO (Generative Engine Optimization), Agentes de IA Autónomos y Arquitecturas de Ultra Baja Latencia</strong>. Las publicaciones estarán disponibles el próximo mes.
            </p>
            <div style="display: flex; justify-content: center; gap: 14px; flex-wrap: wrap;">
              <a href="/#contacto" class="btn btn-gold shimmer-element">
                Agendar Consulta Técnica
              </a>
              <a href="/" class="btn btn-outline">
                Conocer Servicios
              </a>
            </div>
          </div>
        `;
      } else {
        gridContainer.innerHTML = `
          <div style="grid-column: 1 / -1; text-align: center; padding: 60px 20px;" class="glass-panel">
            <h3 style="font-size: 1.3rem; color: var(--brand-gold-light); margin-bottom: 10px;">No se encontraron publicaciones</h3>
            <p style="color: var(--text-muted);">Prueba con otros términos de búsqueda o selecciona otra categoría.</p>
          </div>
        `;
      }
      return;
    }

    gridContainer.innerHTML = filtered.map(post => {
      const postUrl = post.isFirestore ? `/blog/post/?id=${post.id}` : `/blog/${post.id}/`;
      
      const artCover = post.coverImageUrl 
        ? `<div style="width: 80px; height: 80px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(212,175,55,0.4);"><img src="${escapeHtml(post.coverImageUrl)}" alt="Portada" style="width: 100%; height: 100%; object-fit: cover;"></div>`
        : `<div style="width: 80px; height: 80px; border-radius: 50%; border: 2px dashed rgba(212,175,55,0.4); display: flex; align-items: center; justify-content: center;">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d4af37" stroke-width="1.5">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          </div>`;

      return `
        <article class="blog-card glass-card cyber-corners" data-article-id="${post.id}">
          <div class="blog-card-header">
            <div class="blog-card-art">
              ${artCover}
            </div>
            <span class="blog-category-badge">${escapeHtml(post.category)}</span>
          </div>
          <div class="blog-card-body">
            <div class="blog-meta">
              <span class="blog-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                ${escapeHtml(post.readTime)}
              </span>
              <span class="blog-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                ${escapeHtml(post.date)}
              </span>
            </div>
            <h2 class="blog-title"><a href="${postUrl}" style="color: inherit; text-decoration: none;">${escapeHtml(post.title)}</a></h2>
            <p class="blog-excerpt">${escapeHtml(post.excerpt)}</p>
            <a href="${postUrl}" class="blog-read-more" style="text-decoration: none;">
              Leer Artículo Completo
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </a>
          </div>
        </article>
      `;
    }).join('');
  }

  // Filter Buttons
  filterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      filterButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentCategory = btn.getAttribute('data-filter');
      renderPosts();
    });
  });

  // Search input
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      searchQuery = e.target.value;
      renderPosts();
    });
  }

  // Mobile Navigation Drawer Toggle
  const mobileToggle = document.getElementById('mobile-menu-toggle');
  const mobileDrawer = document.getElementById('mobile-drawer');
  const mobileLinks = document.querySelectorAll('.mobile-nav-link');

  if (mobileToggle && mobileDrawer) {
    mobileToggle.addEventListener('click', () => {
      mobileDrawer.classList.toggle('open');
      const isOpen = mobileDrawer.classList.contains('open');
      mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        mobileDrawer.classList.remove('open');
        mobileToggle.setAttribute('aria-expanded', 'false');
      });
    });
  }
});

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
